import React, { useLayoutEffect, useMemo, useRef } from 'react';
import { useTheme } from '@mui/material';
import DSBox from '../../../design-system/DSBox';
import { CalendarEvent } from '../../../api/calendarEvent';
import { isCalendarEventPast } from '../../../utils/calendarEvents';
import CalendarEventListItem from './CalendarEventListItem';

type CalendarEventListViewProps = {
  calendarEvents: CalendarEvent[];
};

const CalendarEventListView = ({
  calendarEvents,
}: CalendarEventListViewProps) => {
  const { spacing } = useTheme();
  const listContainerRef = useRef<HTMLDivElement>(null);
  const scrollTargetEventRef = useRef<HTMLDivElement>(null);

  const scrollTargetEventIndex = useMemo(() => {
    const firstUpcomingEventIndex = calendarEvents.findIndex(
      (event) => !isCalendarEventPast(event),
    );
    if (firstUpcomingEventIndex < 0) {
      return -1;
    }
    return Math.max(firstUpcomingEventIndex - 1, 0);
  }, [calendarEvents]);

  useLayoutEffect(() => {
    const listContainer = listContainerRef.current;
    const scrollTargetEvent = scrollTargetEventRef.current;
    if (!listContainer || !scrollTargetEvent) {
      return;
    }
    listContainer.scrollTo({
      top:
        scrollTargetEvent.getBoundingClientRect().top -
        listContainer.getBoundingClientRect().top +
        listContainer.scrollTop,
      behavior: 'smooth',
    });
  }, [calendarEvents]);

  return (
    <DSBox
      ref={listContainerRef}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: spacing(1),
        padding: spacing(2),
        height: '87vh',
        overflowY: 'auto',
      }}
    >
      {calendarEvents.map((calendarEvent, index) => (
        <CalendarEventListItem
          key={calendarEvent.id}
          ref={index === scrollTargetEventIndex ? scrollTargetEventRef : null}
          calendarEvent={calendarEvent}
        />
      ))}
    </DSBox>
  );
};

export default CalendarEventListView;
